/**
 * Spain 2026 advisor document model (Reddy CASE-001 / advisor.readdy UX).
 * Only Proof of residency and Other accept multiple files per category.
 */

import i18n from '../../i18n';

/** Subtypes for residency & "Other" multi-upload (value → stored as residency_doc_label). */
export const RESIDENCY_DOC_TYPES = [
  { value: 'Padron', label: 'Padrón / Empadronamiento' },
  { value: 'Rental', label: 'Contrato de alquiler' },
  { value: 'Medical', label: 'Historial médico' },
  { value: 'School', label: 'Matrícula escolar' },
  { value: 'Bank', label: 'Extracto bancario' },
  { value: 'Utility', label: 'Factura de suministros' },
  { value: 'Payslip', label: 'Nómina / contrato de trabajo' },
  { value: 'Transfer', label: 'Recibo de transferencia' },
  { value: 'Course', label: 'Certificado de formación' },
  { value: 'Transport', label: 'Billete de transporte' },
  { value: 'Police', label: 'Denuncia policial' },
  { value: 'Official', label: 'Carta oficial' },
  { value: 'Other', label: 'Otro' },
];

export const OTHER_DOC_TYPES = RESIDENCY_DOC_TYPES;

/** Maps UI subtype → Mongo `document_type` (must match backend/document_sections). */
export const RESIDENCY_VALUE_TO_UPLOAD_TYPE = {
  Padron: 'empadronamiento',
  Rental: 'rental',
  Medical: 'medical',
  School: 'school',
  Bank: 'bank',
  Utility: 'utility',
  Payslip: 'employment',
  Transfer: 'transfer',
  Course: 'course',
  Transport: 'transport',
  Police: 'police',
  Official: 'official',
  Other: 'other',
};

export function residencyDocValueToUploadType(value) {
  if (!value) return 'other';
  return RESIDENCY_VALUE_TO_UPLOAD_TYPE[value] || String(value).toLowerCase();
}

export function labelForResidencyDocValue(value) {
  if (!value) return null;
  const row = RESIDENCY_DOC_TYPES.find((t) => t.value === value);
  return row ? row.label : value;
}

export const DOC_CATEGORIES = [
  {
    id: 'passport',
    name: 'Passport',
    icon: 'ri-passport-line',
    description: 'Full passport - all pages. Expired passports accepted.',
    multiple: false,
  },
  {
    id: 'proof_of_residency',
    name: 'Proof of residency',
    icon: 'ri-home-4-line',
    description: 'Documents showing continuous presence in Spain.',
    multiple: true,
  },
  {
    id: 'criminal_record',
    name: 'Criminal record',
    icon: 'ri-shield-check-line',
    description: 'Antecedentes penales - apostilled & translated, less than 3 months old.',
    multiple: false,
  },
  {
    id: 'asylum_receipt',
    name: 'Asylum receipt',
    icon: 'ri-shield-user-line',
    description: 'Resguardo de asilo - filed before 1 Jan 2026.',
    multiple: false,
    note: 'Must be filed before 1 January 2026',
  },
  {
    id: 'other',
    name: 'Other',
    icon: 'ri-file-add-line',
    description: 'Additional supporting documents.',
    multiple: true,
    optional: true,
  },
];

/**
 * Asylum receipt (resguardo) is only required when the case is flagged as
 * "Submitted on or after 1 Jan 2026" (`after`). For `before`, `never`, or unset,
 * hide the slot everywhere counts and checklists are derived.
 */
export function asylumReceiptRequiredForCase(priorAsylumApplication) {
  return priorAsylumApplication === 'after';
}

/** Document rows shown in advisor Documents tab and merged slot lists. */
export function documentCategoriesForPriorAsylum(priorAsylumApplication) {
  if (asylumReceiptRequiredForCase(priorAsylumApplication)) return DOC_CATEGORIES;
  return DOC_CATEGORIES.filter((c) => c.id !== 'asylum_receipt');
}

function localizeDocCategory(cat) {
  const id = cat.id;
  const nameKey = `advisorPortal.docCat_${id}_name`;
  const descKey = `advisorPortal.docCat_${id}_desc`;
  const noteKey = `advisorPortal.docCat_${id}_note`;
  const name = i18n.t(nameKey, { defaultValue: cat.name });
  const description = i18n.t(descKey, { defaultValue: cat.description });
  const note =
    cat.note != null && String(cat.note).trim() !== ''
      ? i18n.t(noteKey, { defaultValue: cat.note })
      : cat.note;
  return { ...cat, name, description, note };
}

/** Required categories only (excludes optional ``Other``). */
export function requiredDocumentCategoriesForCase(caseOrSummary) {
  const c = caseOrSummary || {};
  return documentCategoriesForPriorAsylum(c.prior_asylum_application).filter((cat) => !cat.optional);
}

/** Prefer API `document_required_slots` when present; else derive from required categories (not optional Other). */
export function requiredDocumentSlotTotalForCase(caseOrSummary) {
  const c = caseOrSummary || {};
  const n = Number(c.document_required_slots);
  if (Number.isFinite(n) && n > 0) return n;
  // UI checklist: 4 required slots (identity, residence, criminal, route/asylum); Other is optional.
  const requiredCats = requiredDocumentCategoriesForCase(c);
  if (requiredCats.length >= 4) return 4;
  return requiredCats.length;
}

/** @deprecated Use DOC_CATEGORIES - kept for slot counts in tables. */
export const REQUIRED_DOCS = DOC_CATEGORIES.map((c) => ({
  id: c.id,
  name: c.name,
  description: c.description,
}));

const RESIDENCY_UPLOAD_TYPES = new Set([
  'padron_municipal_registration_id',
  'rental_contract_id',
  'medical_record_id',
  'childrens_school_enrollment_id',
  'bank_statement_id',
  'utility_bill_id',
  'payslip_work_contract_id',
  'money_transfer_receipt_id',
  'course_training_certificate_id',
  'transport_ticket_id',
  'police_report_id',
  'official_letter_notification_id',
  'empadronamiento',
  'rental',
  'medical',
  'bank',
  'utility',
  'employment',
  'other',
  'proof_of_continuous_residence',
  'bank_statements',
  'school',
  'transfer',
  'course',
  'transport',
  'police',
  'official',
]);

const PASSPORT_TYPES = new Set([
  'passport_id',
  'national_id_card_id',
  'travel_document_id',
  'cedula_de_inscripcion_id',
  'cédula_de_inscripción_id',
  'other_id',
  'nationalid',
  'traveldoc',
  'cedulainscripcion',
  'registration',
  'other_identity',
]);

const UK_LEGACY = new Set([
  'income_proof',
  'pay_slips',
  'nie_cert',
  'valuation',
  'employment_contract',
  'tax_return',
  'prior_denial_documents',
]);

export function categoryKeyForDocument(doc = {}) {
  const dt = String(doc.document_type || '').trim();
  const dtl = dt.toLowerCase();
  const sid = String(doc.section_id || '').trim().toLowerCase();

  if (dt === 'criminal_record_certificate' || dtl.includes('criminal')) return 'criminal_record';
  if (sid === 'employment_section') return 'employment';
  if (sid === 'family_section') return 'family';
  if (
    ['application_receipt_id', 'denial_resolution_id', 'red_card_id'].includes(dtl) ||
    sid === 'asylum_section'
  ) {
    return 'asylum_receipt';
  }
  if (dt === 'other_supporting' || sid === 'other_supporting') return 'other';
  if (
    [
      'marriage_certificate_id',
      'libro_de_familia_id',
      'childrens_birth_certificates_id',
      'proof_of_cohabitation_id',
      'school_enrollment_id',
      'other_documents_id',
    ].includes(dtl)
  ) {
    return 'family';
  }
  if (
    [
      'work_contract_id',
      'nomina_id',
      'vida_laboral_id',
      'job_offer_letter_id',
      'other_documents_id',
      'self_employment_id',
    ].includes(dtl)
  ) {
    return 'employment';
  }
  if (RESIDENCY_UPLOAD_TYPES.has(dtl) || sid === 'proof_of_residence_section') {
    return 'proof_of_residency';
  }
  if (sid === 'identity_section' || PASSPORT_TYPES.has(dtl)) return 'passport';
  if (UK_LEGACY.has(dtl)) return 'other';
  if (dt === 'misc' || sid === 'misc') return 'other';
  return 'other';
}

export function statusLabel(reviewStatus) {
  if (reviewStatus === 'accepted') return i18n.t('advisorPortal.reviewAccepted', { defaultValue: 'Accepted' });
  if (reviewStatus === 'rejected') return i18n.t('advisorPortal.reviewRejected', { defaultValue: 'Rejected' });
  if (reviewStatus === 'pending') return i18n.t('advisorPortal.reviewPending', { defaultValue: 'Pending' });
  if (reviewStatus === 'awaiting')
    return i18n.t('advisorPortal.reviewAwaitingUpload', { defaultValue: 'Awaiting upload' });
  return i18n.t('advisorPortal.reviewAwaiting', { defaultValue: 'Awaiting' });
}

function aggregateSlotStatus(files) {
  if (!files.length) return 'awaiting';
  const st = (f) => String(f.review_status || 'pending').toLowerCase();
  if (files.some((f) => st(f) === 'rejected')) return 'rejected';
  if (files.every((f) => st(f) === 'accepted')) return 'accepted';
  return 'pending';
}

function parseIsoMs(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Padron / empadronamiento first, then coverage start, then upload time. */
export function sortFilesForCategory(files, categoryId) {
  if (categoryId !== 'proof_of_residency' && categoryId !== 'other') return [...(files || [])];
  const padronRank = (f) => {
    const dt = String(f?.document_type || '').toLowerCase();
    const lab = String(f?.residency_doc_label || '').toLowerCase();
    if (dt === 'empadronamiento' || lab === 'padron') return 0;
    return 1;
  };
  return [...(files || [])].sort((a, b) => {
    const pr = padronRank(a) - padronRank(b);
    if (pr !== 0) return pr;
    const af = parseIsoMs(a.coverage_date_from);
    const bf = parseIsoMs(b.coverage_date_from);
    if (af != null && bf != null && af !== bf) return af - bf;
    if (af != null && bf == null) return -1;
    if (af == null && bf != null) return 1;
    const ac = parseIsoMs(a.created_at);
    const bc = parseIsoMs(b.created_at);
    return (ac ?? 0) - (bc ?? 0);
  });
}

/**
 * @param {object[]} documents API rows (active or archived).
 * @param {string|undefined} priorAsylumApplication
 * @param {{ archived?: boolean }} [opts]
 */
export function mergeRequiredWithUploads(documents = [], priorAsylumApplication, opts = {}) {
  const { archived = false } = opts;
  const categories = documentCategoriesForPriorAsylum(priorAsylumApplication);
  return categories.map((cat) => {
    const catLoc = localizeDocCategory(cat);
    const hits = (documents || []).filter((d) => categoryKeyForDocument(d) === catLoc.id);
    const files = sortFilesForCategory(
      hits.map((d) => ({
        id: d.id,
        filename: d.filename || d.name || '',
        file_size: d.file_size ?? d.size_bytes ?? null,
        created_at: d.created_at || d.updated_at || null,
        review_status: d.review_status || 'pending',
        rejection_reason: d.rejection_reason || '',
        download_url: d.download_url || null,
        document_type: d.document_type || null,
        coverage_date_from: d.coverage_date_from || null,
        coverage_date_to: d.coverage_date_to || null,
        residency_doc_label: d.residency_doc_label || null,
        supporting_description: d.supporting_description || null,
        _archived: archived,
      })),
      catLoc.id,
    );
    const reviewStatus = aggregateSlotStatus(files);
    const primary = hits[0];
    return {
      ...catLoc,
      files,
      idDb: primary?.id || null,
      uploaded: files.length > 0,
      uploadedFile: primary?.filename || primary?.name || '',
      uploadedAt: primary?.created_at || primary?.updated_at || null,
      reviewStatus,
      rejectionReason: hits.find((h) => h.rejection_reason)?.rejection_reason || '',
    };
  });
}

/** Reddy-style status text for Case Details sidebar document rows. */
export function categorySidebarStatus(cat) {
  const n = cat.files?.length || 0;
  const rs = String(cat.reviewStatus || 'awaiting').toLowerCase();
  const t = (key, opts) => i18n.t(`advisorPortal.${key}`, opts);

  if (cat.optional && n === 0) {
    return { variant: 'none', text: t('sidebarNone', { defaultValue: 'None' }) };
  }
  if (n === 0) {
    return { variant: 'awaiting', text: t('sidebarAwaiting', { defaultValue: 'Awaiting' }) };
  }
  if (cat.multiple) {
    if (rs === 'accepted')
      return { variant: 'complete', text: t('sidebarComplete', { defaultValue: 'Complete' }) };
    return {
      variant: 'partial',
      text: t('sidebarUploadedN', { count: n, defaultValue: `${n} uploaded` }),
    };
  }
  if (rs === 'accepted')
    return { variant: 'complete', text: t('sidebarComplete', { defaultValue: 'Complete' }) };
  if (rs === 'rejected')
    return { variant: 'rejected', text: t('sidebarRejected', { defaultValue: 'Rejected' }) };
  if (rs === 'pending')
    return { variant: 'pending', text: t('sidebarPending', { defaultValue: 'Pending' }) };
  return { variant: 'partial', text: t('sidebarPending', { defaultValue: 'Pending' }) };
}

export function categoryIsComplete(cat) {
  return String(cat.reviewStatus || '').toLowerCase() === 'accepted';
}

/**
 * Icon classes for document category rows (Case Details + Documents tab).
 * Returns null when empty - no grey circle or other placeholder.
 */
export function categorySidebarIconClass(side, uploadedCount = 0) {
  const n = Number(uploadedCount) || 0;
  const variant = side?.variant || 'awaiting';
  if (variant === 'rejected') {
    return 'ri-close-circle-line text-rose-500 text-sm shrink-0';
  }
  if (n > 0 || variant === 'complete') {
    return 'ri-check-line text-emerald-600 text-sm shrink-0';
  }
  return null;
}

const FILE_ICON = {
  pdf: 'ri-file-pdf-line',
  doc: 'ri-file-word-line',
  docx: 'ri-file-word-line',
  jpg: 'ri-image-line',
  jpeg: 'ri-image-line',
  png: 'ri-image-line',
};

export function fileIconClassName(filename) {
  const ext = String(filename || '')
    .split('.')
    .pop()
    ?.toLowerCase() ?? '';
  return FILE_ICON[ext] || 'ri-file-line';
}

const SUBTYPE_LABELS_ES = {
  empadronamiento: 'Padrón / Empadronamiento',
  rental: 'Contrato de alquiler',
  medical: 'Historial médico',
  bank: 'Extracto bancario',
  utility: 'Factura de suministros',
  employment: 'Nómina / contrato de trabajo',
  other: 'Otro',
  school: 'Matrícula escolar',
  transfer: 'Recibo de transferencia',
  course: 'Certificado de formación',
  transport: 'Billete de transporte',
  police: 'Denuncia policial',
  official: 'Carta oficial',
  other_supporting: 'Documento de apoyo',
};

const SUBTYPE_LABELS_EN = {
  empadronamiento: 'Padrón / registration certificate',
  rental: 'Rental contract',
  medical: 'Medical records',
  bank: 'Bank statement',
  utility: 'Utility bill',
  employment: 'Payslip / employment contract',
  other: 'Other',
  school: 'School enrollment',
  transfer: 'Transfer receipt',
  course: 'Training certificate',
  transport: 'Transport ticket',
  police: 'Police report',
  official: 'Official letter',
  other_supporting: 'Supporting document',
};

export function displaySubtypeForFile(file) {
  const label = file?.residency_doc_label;
  if (label) return labelForResidencyDocValue(label) || label;
  const dt = String(file?.document_type || '').toLowerCase();
  const es = String(i18n.language || 'es').toLowerCase().startsWith('es');
  const map = es ? SUBTYPE_LABELS_ES : SUBTYPE_LABELS_EN;
  return map[dt] || (dt ? dt.replace(/_/g, ' ') : null);
}
